import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, Upload, AlertTriangle, Database, RefreshCw, HardDrive, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const fmtBytes = (b) => {
    if (!b) return '0 B';
    const u = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log(b) / Math.log(1024)), u.length - 1);
    return `${(b / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
};

const BackupSection = ({ api }) => {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importReport, setImportReport] = useState(null);
    const [includeBinaries, setIncludeBinaries] = useState(true);

    const loadSummary = async () => {
        try {
            const res = await api.get('/system/backup/summary');
            setSummary(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadSummary(); }, []);

    const doExport = async () => {
        setExporting(true);
        try {
            const res = await api.get(`/system/backup/export?include_binaries=${includeBinaries}`, {
                responseType: 'blob',
                timeout: 0, // große Exporte dürfen dauern
            });
            const disp = res.headers?.['content-disposition'] || '';
            const m = /filename="?([^"]+)"?/.exec(disp);
            const filename = m ? m[1] : `agencyos-backup-${Date.now()}.json`;
            const blob = new Blob([res.data], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click();
            a.remove(); URL.revokeObjectURL(url);
            toast.success(`Backup heruntergeladen (${fmtBytes(blob.size)})`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Fehler beim Export');
        } finally { setExporting(false); }
    };

    const doImport = async (mode) => {
        const input = document.createElement('input');
        input.type = 'file'; input.accept = 'application/json,.json';
        input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;
            const modeLabel = mode === 'replace' ? 'ERSETZT ALLE DATEN' : 'nur bestehende IDs aktualisiert und neue hinzugefügt';
            const ok = window.confirm(
                `Backup-Import starten?\n\nDatei: ${file.name} (${fmtBytes(file.size)})\nModus: ${mode.toUpperCase()}\nEs wird ${modeLabel}.\n\nWirklich fortfahren?`
            );
            if (!ok) return;
            const form = new FormData();
            form.append('file', file);
            setImporting(true);
            setImportReport(null);
            try {
                const res = await api.post(
                    `/system/backup/import?mode=${mode}&confirm=YES-OVERWRITE`,
                    form,
                    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 }
                );
                setImportReport(res.data);
                toast.success(`Import fertig — ${res.data.collections} Collections`);
                await loadSummary();
            } catch (e) {
                toast.error(e?.response?.data?.detail || 'Fehler beim Import');
            } finally { setImporting(false); }
        };
        input.click();
    };

    const included = summary?.included || [];
    const skipped = summary?.skipped || [];
    const counts = summary?.counts || {};
    const sizes = summary?.sizes || {};
    const binaryCols = summary?.binary_collections || [];
    const emptyCritical = summary?.empty_critical || [];
    const missingCritical = summary?.missing_critical || [];
    const totalIncluded = included.reduce((s, n) => s + (counts[n] || 0), 0);
    const estBytes = includeBinaries
        ? (summary?.estimated_bytes || 0)
        : (summary?.estimated_bytes || 0) - (summary?.binary_bytes || 0);

    return (
        <Card data-testid="backup-section">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Database className="w-5 h-5" />
                    Vollständiges Backup
                </CardTitle>
                <CardDescription>
                    Sichert <strong>jede Collection</strong> der Datenbank — Team-Bereich (Kunden, Mitarbeiter, Kalender,
                    Zeiten, Tasks), Kunden-/Content-Bereich (Redaktionspläne, Posts, Ideen, Freigabe-Links) und
                    Social-Face-Abrechnung (Shootings, Rechnungen, Rechnungsnummern-Zähler, PDFs). Neue Bereiche
                    landen automatisch mit im Backup.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {loading ? (
                    <div className="text-sm text-muted-foreground">Lade Übersicht…</div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 rounded-lg border bg-muted/30">
                            <div>
                                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Datenbank</div>
                                <div className="text-sm font-mono truncate">{summary.db_name}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Collections</div>
                                <div className="text-sm">{included.length} <span className="text-muted-foreground">/ {skipped.length} übersprungen</span></div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Dokumente</div>
                                <div className="text-sm font-semibold">{totalIncluded.toLocaleString('de-DE')}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Größe ca.</div>
                                <div className="text-sm font-semibold">{fmtBytes(estBytes)}</div>
                            </div>
                        </div>

                        {(missingCritical.length > 0 || emptyCritical.length > 0) && (
                            <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-xs text-amber-700 dark:text-amber-400 space-y-1">
                                <div className="font-semibold flex items-center gap-1.5">
                                    <AlertTriangle className="w-3.5 h-3.5" /> Hinweis zur Vollständigkeit
                                </div>
                                {missingCritical.length > 0 && (
                                    <div>Noch nicht angelegt: {missingCritical.map((n) => <code key={n} className="mr-1">{n}</code>)}</div>
                                )}
                                {emptyCritical.length > 0 && (
                                    <div>Leer (noch keine Daten): {emptyCritical.map((n) => <code key={n} className="mr-1">{n}</code>)}</div>
                                )}
                                <div className="opacity-80">Das ist normal, solange ein Bereich noch nicht benutzt wurde.</div>
                            </div>
                        )}

                        {binaryCols.length > 0 && (
                            <label className="flex items-start gap-2 p-3 rounded-lg border bg-background text-xs cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={includeBinaries}
                                    onChange={(e) => setIncludeBinaries(e.target.checked)}
                                    className="mt-0.5"
                                    data-testid="backup-include-binaries"
                                />
                                <span>
                                    <span className="font-medium">Rechnungs-PDFs mitsichern</span>{' '}
                                    <span className="text-muted-foreground">
                                        (+{fmtBytes(summary?.binary_bytes || 0)}) — für einen echten Umzug eingeschaltet lassen.
                                        Ohne sie wird die Datei kleiner; die PDFs lassen sich später aus den Rechnungsdaten neu erzeugen.
                                    </span>
                                </span>
                            </label>
                        )}

                        <div className="flex flex-wrap gap-2">
                            <Button onClick={doExport} disabled={exporting} data-testid="backup-export-btn">
                                <Download className={`w-4 h-4 mr-2 ${exporting ? 'animate-bounce' : ''}`} />
                                {exporting ? 'Exportiert…' : 'Backup herunterladen (.json)'}
                            </Button>
                            <Button variant="outline" onClick={() => doImport('merge')} disabled={importing} data-testid="backup-import-merge">
                                <Upload className="w-4 h-4 mr-2" />
                                Import (Merge)
                            </Button>
                            <Button variant="outline" onClick={() => doImport('replace')} disabled={importing} data-testid="backup-import-replace" className="text-rose-600 hover:text-rose-700 hover:bg-rose-500/10">
                                <AlertTriangle className="w-4 h-4 mr-2" />
                                Import (Alles ersetzen)
                            </Button>
                            <Button variant="ghost" size="sm" onClick={loadSummary} data-testid="backup-refresh">
                                <RefreshCw className="w-3.5 h-3.5" />
                            </Button>
                        </div>

                        <details className="text-xs" data-testid="backup-collections-details">
                            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                                Alle {included.length} enthaltenen Collections anzeigen
                            </summary>
                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1 p-3 rounded-lg border bg-background max-h-72 overflow-y-auto">
                                {included.map((n) => (
                                    <div key={n} className="flex items-center justify-between px-2 py-1 rounded hover:bg-muted/60">
                                        <code className="text-[11px] truncate">{n}</code>
                                        <Badge variant="outline" className="text-[10px] font-mono ml-1 flex-shrink-0">
                                            {(counts[n] || 0).toLocaleString('de-DE')}
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                            {skipped.length > 0 && (
                                <div className="mt-2 text-[11px] text-muted-foreground">
                                    Absichtlich übersprungen (transiente Runtime-Daten): {skipped.map((s) => <code key={s} className="mr-1">{s}</code>)}
                                </div>
                            )}
                        </details>

                        <div className="p-3 rounded-lg border border-border bg-muted/30 text-xs space-y-1">
                            <div className="font-semibold flex items-center gap-1.5">
                                <HardDrive className="w-3.5 h-3.5" /> Für einen kompletten Umzug
                            </div>
                            <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground">
                                <li>Dieses Backup enthält <strong>nur die Datenbank</strong>. Hochgeladene Dateien (Bilder, Logos, Unterschriften) liegen im Bucket und müssen separat kopiert werden.</li>
                                <li>Der Export enthält <strong>Passwort-Hashes</strong> — Datei sicher behandeln, nicht per Mail verschicken.</li>
                                <li><strong>Merge</strong>: bestehende IDs werden aktualisiert, neue eingefügt, nichts gelöscht. <strong>Alles ersetzen</strong>: pro Collection erst leeren, dann einfügen — für ein frisches Zielsystem.</li>
                                <li>Schritt-für-Schritt-Anleitung für den Serverwechsel: <code>UMZUG.md</code> im Projekt.</li>
                            </ul>
                        </div>

                        {importReport && (
                            <div className="p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-xs" data-testid="backup-import-report">
                                <div className="font-semibold mb-1 flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400">
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    Import abgeschlossen — {importReport.collections} Collections ({importReport.mode})
                                </div>
                                <div className="max-h-60 overflow-y-auto font-mono text-[11px] space-y-0.5">
                                    {Object.entries(importReport.report).map(([col, r]) => (
                                        <div key={col} className="flex justify-between">
                                            <span>{col}</span>
                                            <span>{Object.entries(r).map(([k, v]) => `${k}=${v}`).join(' · ')}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default BackupSection;
